import { DocumentNode, gql } from '@apollo/client';
import { RankInput } from 'sr-types/lib/search/v1/graphql';
import { useMutationWithContext } from '../hooks/useMutationWithContext';
import { failedToastMessage, successToastMessage } from '../utils/commonUtils';
import { searchClient } from './slimQuery';

//mutation to save a single rank
export const saveRankQuery: DocumentNode = gql`
    mutation SaveRank($input: RankInput) {
        rank(input: $input) {
            id
            errors {
                field
                message
            }
        }
    }
`;

//mutation to save multiple ranks
export const saveRanksQuery: DocumentNode = gql`
    mutation SaveRanks($input: [RankInput]!) {
        ranks(input: $input) {
            id
            errors {
                field
                message
            }
        }
    }
`;

//hook to save a single rank
export const useSaveRank = (successMessageToken: string, failureMessageToken: string) => {
    const [saveRank, { loading: isRankSaving }] = useMutationWithContext(saveRankQuery, searchClient);

    const onSaveRank = async (input: RankInput): Promise<string | null> => {
        try {
            const { data } = await saveRank({
                variables: {
                    input
                }
            });

            const errors = data?.rank?.errors;
            const id = data?.rank?.id;

            if (errors) {
                failedToastMessage(failureMessageToken);
                return null;
            }

            successToastMessage(successMessageToken);
            return id;
        } catch (err) {
            failedToastMessage(failureMessageToken);
            return err;
        }
    };

    return { onSaveRank, isRankSaving };
};

//hook to save multiple ranks
export const useSaveRanks = (successMessageToken: string, failureMessageToken: string) => {
    const [saveRanks, { loading: isRanksSaving }] = useMutationWithContext(saveRanksQuery, searchClient);

    const onSaveRanks = async (
        input: RankInput[],
    ): Promise<string[] | null> => {
        try {
            const { data } = await saveRanks({
                variables: {
                    input
                }
            });

            const errors = (data?.ranks ?? []).flatMap((rank) => rank.errors ?? []).filter(Boolean);
            const ids = data?.ranks?.map((rank) => rank.id);

            if (errors?.length) {
                failedToastMessage(failureMessageToken);
                return null;
            }

            successToastMessage(successMessageToken);
            return ids;
        } catch (err) {
            failedToastMessage(failureMessageToken);
            return err;
        }
    };

    return { onSaveRanks, isRanksSaving };
};
